import { useMemo, useEffect, useState, RefObject } from 'react';
import debounce from 'lodash/debounce';

/**
 * 計算縮放比例
 */

export interface UseResize {
  x: number;
  y: number;
}

interface ResizeConfig {
  width: number;
  height: number;
}

const SCALE_DATA: UseResize = {
  x: 1,
  y: 1
};
const DEBOUNCE_TIME = 1;

export default function useResize(
  containerRef: RefObject<HTMLElement> | null = null,
  targetContainerRef: RefObject<HTMLElement> | null = null,
  fireWidth = 768,
  firedResized?: unknown
): UseResize {
  /* Global & Local State */
  const [resizeScale, setRecentScale] = useState<UseResize>(SCALE_DATA);

  /* Functions */
  const onResize = (event?: UIEvent, config: ResizeConfig | null = null): void => {
    if (!containerRef?.current || !targetContainerRef?.current) return;

    const width = config ? config.width : containerRef.current.offsetWidth;

    // 如果寬度 >= fireWidth，直接設定為 1,1
    if (width >= fireWidth) {
      setRecentScale({ x: 1, y: 1 });
      return;
    }

    const scale = Math.min(width / fireWidth, 1);
    setRecentScale(prev => (prev.x === scale && prev.y === scale ? prev : { x: scale, y: scale }));
  };

  const onDebounceResize = debounce((event?: UIEvent, config: ResizeConfig | null = null) => {
    onResize(event, config);
  }, DEBOUNCE_TIME);

  const registerResize = (): void => {
    if (typeof window !== 'undefined') window.addEventListener('resize', onDebounceResize, false);
  };
  const unregisterResize = (): void => {
    if (typeof window !== 'undefined') window.removeEventListener('resize', onDebounceResize, false);
  };

  /* Hooks */
  useEffect(() => {
    registerResize();
    onDebounceResize();
    return () => {
      unregisterResize();
      onDebounceResize.cancel();
    };
  }, []);

  useEffect(() => {
    if (containerRef?.current && targetContainerRef?.current) {
      onResize(undefined, { width: containerRef.current.offsetWidth, height: containerRef.current.offsetHeight });
    }
  }, [containerRef?.current, targetContainerRef?.current, firedResized]);

  /* Main */
  return useMemo<UseResize>(() => resizeScale, [resizeScale.x, resizeScale.y]);
}
